/* =========================================================
   Glücksrad (Wheel of Fortune)
   - <gluecksrad-drawer> extends the theme's DrawerElement
     (left slide, mobile bottom-sheet, focus-trap, scroll-lock,
      escape, gesture-close are all inherited – not re-implemented)
   - Canvas wheel, weighted win, client-side Klaviyo, console logging,
     play-once, dynamic [[WIN]] token.
   ========================================================= */
(function () {
  const KLAVIYO_REVISION = '2026-04-15';
  const SPIN_DURATION = 5.2; // seconds
  const FULL_SPINS = 6;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function defineGluecksrad() {
    if (customElements.get('gluecksrad-drawer')) return;
    customElements.define(
    'gluecksrad-drawer',
    class GluecksradDrawer extends DrawerElement {
      constructor() {
        super();
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.initialized) return;
        this.initialized = true;

        if (window.location.pathname === '/challenge') return;

        this.config = this.parseConfig();
        // `wins` should be a parsed array; tolerate a JSON string just in case
        if (this.config && typeof this.config.wins === 'string') {
          try { this.config.wins = JSON.parse(this.config.wins); } catch (e) {}
        }
        this.devMode = !!(this.config && this.config.devMode);
        if (this.devMode) this.log('Dev-Modus aktiv – Cookie/Gewinn-Speicher werden ignoriert.');

        if (!this.config || !Array.isArray(this.config.wins) || !this.config.wins.length) {
          this.warn('Keine gültigen Wins – Rad wird nicht initialisiert.');
          return;
        }

        this.cookieName = 'DatoraWheel';
        this.stateKey = `DatoraWheel:${this.config.version || 'a'}:${this.config.handle || 'x'}`;
        this.sessionKey = `DatoraWheel:closed:${this.config.version || 'a'}:${this.config.handle || 'x'}`;
        this.email = '';
        this.spun = false;
        this.spinning = false;
        this.rotation = 0;
        this.answers = {}; // qid -> answer (for conditional questions)

        this.buildWins();
        this.cacheElements();
        this.collectWinTokens();
        this.replaceWinTokens(this.maxWinLabel); // pre-spin fallback = highest prize
        this.setupCanvas();
        this.bindEvents();
        this.bindQuestions();
        this.restoreState();
        this.maybeAutoOpen();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
      }

      /* ---------- Config ---------- */
      parseConfig() {
        const el = this.querySelector('[data-gluecksrad-config]');
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (err) {
          return null;
        }
      }

      /* ---------- Wins / segments ---------- */
      buildWins() {
        this.wins = this.config.wins.map((w, i) => ({
          chance: Number(w.chance) || 0,
          title: String(w.title || ''),
          coupon: w.coupon ? String(w.coupon) : '',
          index: i
        }));

        this.eligible = this.wins.filter((w) => w.chance > 0);
        this.noWinner = this.eligible.length === 0;
        if (this.noWinner) this.warn('Kein Gewinn mit chance > 0 – Spin deaktiviert.');

        // Segment count = wins_fields, repeated cyclically; never fewer than the win list
        let count = parseInt(this.config.winsFields, 10);
        if (!count || count < this.wins.length) count = this.wins.length;
        this.segments = [];
        for (let i = 0; i < count; i++) this.segments.push(this.wins[i % this.wins.length]);

        this.maxWinLabel = this.computeMaxWin();
      }

      computeMaxWin() {
        let best = this.wins[0], bestNum = -Infinity;
        this.wins.forEach((w) => {
          const m = w.title.match(/\d+/);
          const num = m ? parseInt(m[0], 10) : -1;
          if (num > bestNum) { bestNum = num; best = w; }
        });
        return best ? best.title : '';
      }

      pickWinner() {
        const sum = this.eligible.reduce((s, w) => s + w.chance, 0);
        let r = Math.random() * sum;
        for (const w of this.eligible) { r -= w.chance; if (r <= 0) return w; }
        return this.eligible[this.eligible.length - 1];
      }

      pickSegmentFor(originalIndex) {
        const matches = [];
        this.segments.forEach((s, i) => { if (s.index === originalIndex) matches.push(i); });
        return matches.length ? matches[Math.floor(Math.random() * matches.length)] : 0;
      }

      /* ---------- DOM ---------- */
      cacheElements() {
        this.form = this.querySelector('.gluecksrad__form');
        this.emailInput = this.querySelector('.gluecksrad__email');
        this.errorEl = this.querySelector('[data-gluecksrad-error]');
        this.spinBtn = this.querySelector('.gluecksrad__spin');
        this.canvas = this.querySelector('.gluecksrad-canvas');
        this.rotor = this.querySelector('.gluecksrad-rotor');
        this.pointer = this.querySelector('.gluecksrad-pointer');
        this.live = this.querySelector('[data-gluecksrad-live]');
      }

      get focusElement() {
        if (this.getAttribute('data-page') === '2') {
          return this.querySelector('.gluecksrad-page-2 .gluecksrad__headline') || this.querySelector('.gluecksrad-page-2 button');
        }
        return this.emailInput || this.querySelector('button');
      }

      /* ---------- [[WIN]] token ---------- */
      collectWinTokens() {
        this._winNodes = [];
        const roots = [this];
        const sticky = document.querySelector(`.gluecksrad-sticky[aria-controls="${this.id}"]`) || document.querySelector('.gluecksrad-sticky');
        if (sticky) roots.push(sticky);
        roots.forEach((root) => {
          const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
          let n;
          while ((n = walker.nextNode())) {
            if (n.nodeValue && n.nodeValue.indexOf('[[WIN]]') !== -1) {
              this._winNodes.push({ node: n, tpl: n.nodeValue });
            }
          }
        });
      }

      replaceWinTokens(value) {
        (this._winNodes || []).forEach(({ node, tpl }) => {
          node.nodeValue = tpl.replace(/\[\[WIN\]\]/g, value);
        });
      }

      /* ---------- Canvas ---------- */
      setupCanvas() {
        if (!this.canvas) return;
        this.ctx = this.canvas.getContext('2d');
        this._resizeHandler = this.debounce(() => this.resize(), 150);
        window.addEventListener('resize', this._resizeHandler);
        this.resize();
      }

      readColors() {
        const cs = getComputedStyle(this);
        const v = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
        this.segColors = (this.config.segColors && this.config.segColors.length)
          ? this.config.segColors
          : [v('--wheel-seg-1', '#6E8181'), v('--wheel-seg-2', '#626262'), v('--wheel-seg-3', '#6E8181'), v('--wheel-seg-4', '#626262')];
        this.labelColor = v('--wheel-label-text', '#ffffff');
        this.borderColor = v('--wheel-border', '#ffffff');
        this.tickerColor = v('--wheel-ticker', '#e0523e');
        this.dotsColor = v('--wheel-dots', '#ffffff');
        this.sticksColor = v('--wheel-sticks', '#6E8181');
        this.fontFamily = cs.fontFamily || 'sans-serif';
      }

      resize() {
        if (!this.ctx) return;
        // clientWidth = layout width (ignores the rotor's rotate transform); getBoundingClientRect would
        // return the enlarged bounding box of the rotated canvas and distort the redraw after a spin.
        const size = Math.round(this.canvas.clientWidth);
        if (size < 2) return; // not visible yet (drawer closed / on page 2)
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = size * dpr;
        this.canvas.height = size * dpr;
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this._size = size;
        this.readColors();
        this.draw();
      }

      draw() {
        if (!this.ctx || !this._size) return;
        const ctx = this.ctx;
        const size = this._size;
        const cx = size / 2, cy = size / 2;
        const r = size / 2;
        const n = this.segments.length;
        const seg = (Math.PI * 2) / n;
        const ringW = Math.max(4, size * 0.03); // white outer ring thickness (thin)
        const rInner = r - ringW;               // colored wedges end here, ring sits on top
        const overlap = 0.6 * (Math.PI / 180);  // tiny angular overlap to kill anti-alias seams

        ctx.clearRect(0, 0, size, size);
        ctx.lineJoin = 'round';

        // 1) Colored wedges (overlapping slightly so there are no hairline gaps between them)
        for (let i = 0; i < n; i++) {
          const start = i * seg;
          const end = (i + 1) * seg + overlap;
          ctx.beginPath();
          ctx.moveTo(cx, cy);
          ctx.arc(cx, cy, rInner, start, end);
          ctx.closePath();
          ctx.fillStyle = this.segColors[i % this.segColors.length];
          ctx.fill();
        }

        // 2) Labels
        for (let i = 0; i < n; i++) {
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(i * seg + seg / 2);
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = this.labelColor;
          ctx.font = `600 ${Math.max(11, Math.round(size * 0.033))}px ${this.fontFamily}`;
          ctx.fillText(this.segments[i].title, rInner - size * 0.04, 0);
          ctx.restore();
        }

        // 3) White outer ring (covers the wedge outer edge cleanly)
        ctx.beginPath();
        ctx.arc(cx, cy, r - ringW / 2, 0, Math.PI * 2);
        ctx.lineWidth = ringW;
        ctx.strokeStyle = this.borderColor;
        ctx.stroke();

        // 4) Dots at the outer end of each segment boundary, just inside the colored area
        //    (so white dots stay visible on the segments, exactly where the pointer tip meets the rim)
        const dotR = rInner - size * 0.02;
        for (let i = 0; i < n; i++) {
          const a = i * seg;
          ctx.beginPath();
          ctx.arc(cx + Math.cos(a) * dotR, cy + Math.sin(a) * dotR, Math.max(2, size * 0.011), 0, Math.PI * 2);
          ctx.fillStyle = this.dotsColor;
          ctx.fill();
        }

        // 5) Center hub
        ctx.beginPath();
        ctx.arc(cx, cy, size * 0.09, 0, Math.PI * 2);
        ctx.fillStyle = this.tickerColor;
        ctx.fill();
      }

      /* ---------- Events ---------- */
      bindEvents() {
        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
        if (this.emailInput) this.emailInput.addEventListener('input', () => this.clearError());
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.spun || this.spinning) return;
        const input = this.emailInput;
        this.clearError();
        if (input && !input.checkValidity()) { input.reportValidity(); return; }
        const email = (input ? input.value : '').trim();

        // Duplicate check against the Klaviyo list – requires a server proxy (private key);
        // only runs when enabled in the theme settings AND a proxy URL is set.
        if (this.config.emailCheckEnabled && this.config.emailCheckUrl) {
          if (this.spinBtn) { this.spinBtn.disabled = true; this.spinBtn.setAttribute('aria-busy', 'true'); }
          let exists = false;
          try { exists = await this.emailExists(email); } catch (e) { exists = false; }
          if (this.spinBtn) { this.spinBtn.disabled = false; this.spinBtn.removeAttribute('aria-busy'); }
          if (exists) {
            this.log('email bereits in Liste:', email);
            this.showError(this.config.emailExistsError || 'Diese E-Mail nimmt bereits teil.');
            return; // no spin
          }
        }

        this.email = email;
        this.log('email:', this.email);
        this.klaviyoSubscribe(this.email, {
          gluecksrad_status: 'entered',
          gluecksrad_version: this.config.version || 'a'
        });
        this.spin();
      }

      async emailExists(email) {
        const url = this.config.emailCheckUrl;
        const sep = url.indexOf('?') === -1 ? '?' : '&';
        const res = await fetch(url + sep + 'email=' + encodeURIComponent(email), { headers: { accept: 'application/json' } });
        if (!res.ok) return false;
        const data = await res.json().catch(() => ({}));
        return !!data.exists;
      }

      showError(msg) {
        if (this.errorEl) { this.errorEl.textContent = msg || ''; this.errorEl.hidden = false; }
        if (this.emailInput) this.emailInput.setAttribute('aria-invalid', 'true');
      }
      clearError() {
        if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
        if (this.emailInput) this.emailInput.removeAttribute('aria-invalid');
      }

      /* ---------- Spin ---------- */
      spin() {
        if (this.spinning || this.spun) return;
        if (this.noWinner) { this.warn('Spin nicht möglich – kein gültiger Gewinn.'); return; }
        this.spinning = true;
        if (this.spinBtn) { this.spinBtn.setAttribute('aria-busy', 'true'); this.spinBtn.disabled = true; }

        // Cookie is set on SPIN (not on close): once the user spun, don't auto-open again.
        // expiry 0 = no lifetime → never persist (wheel keeps coming back).
        const exp = Number(this.config.expiry);
        if (!this.devMode && !(window.Shopify && Shopify.designMode) && exp > 0) {
          this.setCookie(this.cookieName, exp);
        }

        const winner = this.pickWinner();
        const segIdx = this.pickSegmentFor(winner.index);
        const segAngle = 360 / this.segments.length;
        const segCenter = (segIdx + 0.5) * segAngle;          // pointer sits at 0° (right)
        const base = (((-segCenter) % 360) + 360) % 360;
        const cur = this.rotation;
        const jitter = (Math.random() - 0.5) * segAngle * 0.5; // stay inside the winning wedge
        const target = cur + 360 * FULL_SPINS + (((base - (cur % 360)) + 360) % 360) + jitter;
        this.rotation = target;

        let done = false;
        const finish = () => { if (done) return; done = true; this.onWin(winner); };

        if (reduceMotion || !this.rotor) {
          if (this.rotor) {
            this.rotor.style.transition = 'none';
            this.rotor.style.transform = `rotate(${target}deg)`;
          }
          finish();
          return;
        }

        // rAF-driven spin so we can ratchet the pointer on every peg (like a real wheel)
        this.rotor.style.transition = 'none';
        const startDeg = cur;
        const totalDeg = target - cur;
        const dur = SPIN_DURATION * 1000;
        const segDeg = segAngle;
        const ease = (x) => 1 - Math.pow(1 - x, 4); // easeOutQuart
        const STIFF = 300, DAMP = 18, KICK = 520, MAX_FLIP = 34; // KICK ≈ Grad × √STIFF (≈17.3) → ~30°
        let t0 = null, lastTs = null, lastPeg = Math.floor(startDeg / segDeg);
        let pAngle = 0, pVel = 0;
        const setFlip = () => { if (this.pointer) this.pointer.style.setProperty('--pointer-flip', pAngle.toFixed(2) + 'deg'); };

        const step = (ts) => {
          if (t0 === null) { t0 = ts; lastTs = ts; }
          const dt = Math.min(0.032, (ts - lastTs) / 1000) || 0.016;
          lastTs = ts;
          const p = Math.min(1, (ts - t0) / dur);
          const deg = startDeg + totalDeg * ease(p);
          this.rotor.style.transform = `rotate(${deg}deg)`;

          // one flick DOWN per rim-dot that passes under the pointer (dots travel top→bottom at 3 o'clock).
          // Set (not add) the kick velocity so every flip is the same size regardless of wheel speed.
          const peg = Math.floor(deg / segDeg);
          if (peg !== lastPeg) { pVel = -KICK; lastPeg = peg; }
          // damped spring back to rest
          pVel += (-STIFF * pAngle - DAMP * pVel) * dt;
          pAngle += pVel * dt;
          if (pAngle > MAX_FLIP) { pAngle = MAX_FLIP; pVel = 0; }
          else if (pAngle < -MAX_FLIP) { pAngle = -MAX_FLIP; pVel = 0; }
          setFlip();

          if (p < 1 || Math.abs(pAngle) > 0.1 || Math.abs(pVel) > 0.1) {
            requestAnimationFrame(step);
            return;
          }
          pAngle = 0; pVel = 0; setFlip();
          finish();
        };
        requestAnimationFrame(step);
        // safety net if rAF is paused (inactive tab)
        setTimeout(finish, dur + 800);
      }

      onWin(winner) {
        this.spinning = false;
        this.spun = true;
        const coupon = winner.coupon && !/^prizecode/i.test(winner.coupon) ? winner.coupon : '';
        this.log('win:', winner.title, coupon ? `(Coupon: ${coupon})` : '');

        const props = { coupon_title: winner.title };
        if (coupon) props.coupon_code = coupon;
        this.klaviyoUpdate(this.email, props);

        if (!this.devMode && Number(this.config.expiry) > 0) {
          this.persistState({ won: true, title: winner.title, coupon: this.encode(coupon) });
        }

        this.replaceWinTokens(winner.title);
        if (this.live) this.live.textContent = `Gewonnen: ${winner.title}`;
        this.showPage(2);
      }

      showPage(page) {
        this.setAttribute('data-page', String(page));
        if (page === 2) {
          const h = this.querySelector('.gluecksrad-page-2 .gluecksrad__headline') || this.querySelector('.gluecksrad-page-2 h2');
          if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
        }
      }

      /* ---------- Questions (page 2) ---------- */
      bindQuestions() {
        this.querySelectorAll('.gluecksrad__question').forEach((fs) => {
          const qid = fs.getAttribute('data-qid') || '';
          const type = fs.getAttribute('data-type') || 'one-select';
          const question = fs.getAttribute('data-question') || '';
          const klaviyoField = (fs.getAttribute('data-klaviyo-field') || '').trim();
          const key = klaviyoField || 'gluecksrad_q_' + this.slugify(question);

          if (type === 'one-select' || type === 'multi-select') {
            const opts = Array.from(fs.querySelectorAll('.gluecksrad__option'));
            opts.forEach((btn) => {
              btn.addEventListener('click', () => {
                const val = btn.getAttribute('data-value');
                if (type === 'one-select') {
                  opts.forEach((b) => {
                    const on = b === btn;
                    b.classList.toggle('is-selected', on);
                    b.setAttribute('aria-pressed', on ? 'true' : 'false');
                  });
                  this.recordAnswer(qid, question, key, val);
                } else {
                  const on = !btn.classList.contains('is-selected');
                  btn.classList.toggle('is-selected', on);
                  btn.setAttribute('aria-pressed', on ? 'true' : 'false');
                  const sel = opts.filter((b) => b.classList.contains('is-selected')).map((b) => b.getAttribute('data-value'));
                  this.recordAnswer(qid, question, key, sel);
                }
              });
            });
          } else {
            const input = fs.querySelector('.gluecksrad__answer-input');
            if (input) input.addEventListener('change', () => {
              const val = input.value.trim();
              if (val) this.recordAnswer(qid, question, key, val);
            });
          }
        });
        this.evaluateConditions(); // set initial visibility of dependent questions
      }

      recordAnswer(qid, question, key, value) {
        if (qid) this.answers[qid] = value;
        this.log('answer', { question: question, answer: value, 'klaviyo-field': key });
        this.klaviyoUpdate(this.email, { [key]: value });
        this.evaluateConditions();
      }

      /* ---------- Conditional questions (depends_on / depends_on_value) ---------- */
      evaluateConditions() {
        const fss = Array.from(this.querySelectorAll('.gluecksrad__question[data-depends-on]'));
        // Re-run to a fixed point: hiding a question clears its answer, which can in turn
        // hide questions that depend on IT (chains), regardless of DOM order.
        let changed = true;
        let guard = 0;
        while (changed && guard++ < 12) {
          changed = false;
          fss.forEach((fs) => {
            const parentId = fs.getAttribute('data-depends-on');
            const values = (fs.getAttribute('data-depends-values') || '').split('|').map((s) => s.trim()).filter(Boolean);
            const show = this.conditionMet(this.answers[parentId], values);
            if (show && fs.hidden) {
              fs.hidden = false;
              changed = true;
            } else if (!show && !fs.hidden) {
              this.clearQuestion(fs); // becoming hidden again → reset its answer/UI
              fs.hidden = true;
              changed = true;
            }
          });
        }
      }

      conditionMet(ans, values) {
        const has = Array.isArray(ans) ? ans.length > 0 : (typeof ans === 'string' && ans.trim() !== '');
        if (!has) return false;
        if (!values.length) return true; // any answer is enough
        const norm = (s) => String(s).trim().toLowerCase();
        const vals = values.map(norm);
        return Array.isArray(ans) ? ans.some((a) => vals.includes(norm(a))) : vals.includes(norm(ans));
      }

      clearQuestion(fs) {
        const qid = fs.getAttribute('data-qid');
        if (qid) delete this.answers[qid];
        fs.querySelectorAll('.gluecksrad__option.is-selected').forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        const input = fs.querySelector('.gluecksrad__answer-input');
        if (input) input.value = '';
      }

      /* ---------- Klaviyo (client-side) ---------- */
      async klaviyoSubscribe(email, properties) {
        if (!this.config.companyId || !this.config.listId) {
          this.warn('Klaviyo-Subscribe übersprungen – Company-ID oder List-ID fehlt.');
          return;
        }
        if (!email) return;
        const body = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Glücksrad',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    properties: properties || {},
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: this.config.listId } } }
          }
        };
        try {
          const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.config.companyId)}`, {
            method: 'POST',
            headers: { revision: KLAVIYO_REVISION, 'content-type': 'application/json' },
            body: JSON.stringify(body)
          });
          this.log('Klaviyo subscribe →', res.status, properties);
        } catch (err) {
          this.warn('Klaviyo subscribe Fehler:', err);
        }
      }

      async klaviyoUpdate(email, properties) {
        if (!this.config.companyId) {
          this.warn('Klaviyo-Update übersprungen – Company-ID fehlt.');
          return;
        }
        if (!email) return;
        const body = { data: { type: 'profile', attributes: { email: email, properties: properties || {} } } };
        try {
          const res = await fetch(`https://a.klaviyo.com/client/profiles?company_id=${encodeURIComponent(this.config.companyId)}`, {
            method: 'POST',
            headers: { revision: KLAVIYO_REVISION, 'content-type': 'application/json' },
            body: JSON.stringify(body)
          });
          this.log('Klaviyo update →', res.status, properties);
        } catch (err) {
          this.warn('Klaviyo update Fehler:', err);
        }
      }

      /* ---------- Open / state ---------- */
      maybeAutoOpen() {
        if (window.Shopify && Shopify.designMode) {
          this.log('Design-Mode: Auto-Open deaktiviert – im Storefront testen oder die Sektion im Editor auswählen.');
          return; // base handles section select/deselect in the editor
        }
        if (this.spun) return; // already completed (restored win-state) → don't auto-pop again
        const delay = Number(this.config.delay);
        if (isNaN(delay) || delay <= 0) return; // 0 = sticky button only
        if (!this.devMode && this.getCookie(this.cookieName)) {
          this.log('Auto-Open übersprungen – Cookie "' + this.cookieName + '" gesetzt.');
          return;
        }
        if (!this.devMode && this.config.sessionSuppress && this.getSession(this.sessionKey)) {
          this.log('Auto-Open übersprungen – in dieser Session bereits geschlossen.');
          return;
        }
        setTimeout(() => {
          if (this.open) return;
          // Cross-feature priority: coordinate with an open Cart Reminder overlay.
          const cr = document.querySelector('cart-reminder-modal');
          if (cr && cr.open) {
            if ((this.config.priority || 'gluecksrad') === 'gluecksrad') {
              // wheel wins → close the reminder untouched (it may trigger again later)
              if (typeof cr.dismissForPriority === 'function') cr.dismissForPriority();
              else if (typeof cr.hide === 'function') cr.hide();
            } else {
              // CR wins → suppress the auto-open now, but retry once the reminder closes
              // (gates were already passed when the timer was armed).
              this.log('Auto-Open unterdrückt – Cart Reminder gewinnt (Priorität).');
              cr.addEventListener('modal:afterHide', () => { if (!this.open && !this.spun) this.show(); }, { once: true });
              return;
            }
          }
          this.show();
        }, delay * 1000);
      }

      restoreState() {
        if (this.devMode) return; // never restore the won-state while testing
        const st = this.readState();
        if (st && st.won) {
          this.spun = true;
          if (this.spinBtn) this.spinBtn.disabled = true;
          this.replaceWinTokens(st.title || this.maxWinLabel);
          this.showPage(2);
        }
      }

      show(activeElement = null, animate = true) {
        if (this.devMode) this.resetForDev(); // fresh start on every open while testing
        return super.show(activeElement, animate);
      }

      resetForDev() {
        this.spun = false;
        this.spinning = false;
        this.rotation = 0;
        this.setAttribute('data-page', '1');
        if (this.rotor) {
          this.rotor.style.transition = 'none';
          this.rotor.style.transform = 'rotate(0deg)';
        }
        if (this.pointer) this.pointer.style.setProperty('--pointer-flip', '0deg');
        if (this.spinBtn) {
          this.spinBtn.disabled = false;
          this.spinBtn.removeAttribute('aria-busy');
        }
        this.answers = {};
        this.querySelectorAll('.gluecksrad__option.is-selected').forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        this.querySelectorAll('.gluecksrad__answer-input').forEach((i) => { i.value = ''; });
        this.clearSession(this.sessionKey);
        this.clearError();
        if (this._winNodes) this.replaceWinTokens(this.maxWinLabel); // reset [[WIN]] to pre-spin value
        this.evaluateConditions();
      }

      afterShow() {
        if (!this.open) return; // dismissed (e.g. by priority) before the transition resolved → skip lock/focus
        super.afterShow();
        this.resize(); // canvas now has dimensions
      }

      afterHide() {
        super.afterHide();
        // Closed by the other feature (priority) → leave state untouched so it can auto-open again later.
        if (this._silentClose) { this._silentClose = false; return; }
        // No persistent cookie on close — only spinning does that.
        // But (optionally) suppress re-opening for the rest of this session.
        if (!this.devMode && !(window.Shopify && Shopify.designMode) && this.config.sessionSuppress) {
          this.setSession(this.sessionKey);
        }
      }

      // Close because the other overlay won the priority – no session-suppress side effect.
      dismissForPriority() {
        this._silentClose = true;
        return this.hide();
      }

      /* ---------- Helpers ---------- */
      persistState(obj) {
        try { localStorage.setItem(this.stateKey, JSON.stringify(obj)); } catch (e) {}
      }
      readState() {
        try { return JSON.parse(localStorage.getItem(this.stateKey) || 'null'); } catch (e) { return null; }
      }
      // Reversible obfuscation so the coupon is never stored in plain text (XOR + base64).
      get cipherKey() { return 'datora-gluecksrad-' + (this.config.handle || 'x'); }
      encode(str) {
        if (!str) return '';
        try {
          const key = this.cipherKey;
          let out = '';
          for (let i = 0; i < str.length; i++) {
            out += String.fromCharCode(str.charCodeAt(i) ^ key.charCodeAt(i % key.length));
          }
          return btoa(unescape(encodeURIComponent(out)));
        } catch (e) { return ''; }
      }
      decode(enc) {
        if (!enc) return '';
        try {
          const key = this.cipherKey;
          const raw = decodeURIComponent(escape(atob(enc)));
          let out = '';
          for (let i = 0; i < raw.length; i++) {
            out += String.fromCharCode(raw.charCodeAt(i) ^ key.charCodeAt(i % key.length));
          }
          return out;
        } catch (e) { return ''; }
      }
      getStoredCoupon() {
        const st = this.readState();
        return st && st.coupon ? this.decode(st.coupon) : '';
      }
      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name.replace(/[-:]/g, '\\$&')}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }
      setCookie(name, days) {
        document.cookie = `${name}=true; max-age=${days * 24 * 60 * 60}; path=/`;
      }
      getSession(key) { try { return sessionStorage.getItem(key); } catch (e) { return null; } }
      setSession(key) { try { sessionStorage.setItem(key, '1'); } catch (e) {} }
      clearSession(key) { try { sessionStorage.removeItem(key); } catch (e) {} }
      slugify(text) {
        return String(text || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 40);
      }
      debounce(fn, wait) {
        let t;
        return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
      }
      // Console output only when Dev-Mode is enabled in the theme settings
      log(...args) { if (this.devMode) console.log('[Glücksrad]', ...args); }
      warn(...args) { if (this.devMode) console.warn('[Glücksrad]', ...args); }
    }
    );
  }

  // Define as soon as the theme's DrawerElement base class is available (defer load-order safety).
  let _tries = 0;
  (function whenReady() {
    if (typeof DrawerElement !== 'undefined') { defineGluecksrad(); return; }
    if (_tries++ > 100) { console.warn('[Glücksrad] DrawerElement nicht verfügbar – Rad inaktiv.'); return; }
    setTimeout(whenReady, 50);
  })();
})();
